import React, {useState, useEffect, useCallback} from 'react';
//import {errorHandler} from "./errors";

// Prefix for the same-document sync event. The native `storage` event only
// fires in *other* tabs, so we dispatch our own event to keep every hook
// instance in the current document in sync (e.g. the voting confirmation
// banner updating the gallery so the user can no longer vote).
const SYNC_EVENT_PREFIX = 'local-storage-sync:';

export default function useLocalStorage(key, initialValue) {
  const readValue = useCallback(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      //errorHandler(error);
      return initialValue;
    }
  }, [key]);

  const [storedValue, setStoredValue] = useState(readValue);

  const setValue = useCallback(value => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      // Allow value to be a function so we have same API as useState. Base the
      // functional update on the freshly persisted value rather than this
      // instance's (possibly stale) state, so updates from other hook
      // instances are not clobbered.
      const valueToStore =
        value instanceof Function ? value(readValue()) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
      // Notify the other hook instances in this document.
      window.dispatchEvent(new CustomEvent(SYNC_EVENT_PREFIX + key));
    } catch (error) {
      //errorHandler(error);
    }
  }, [key, readValue]);

  useEffect(() => {
    const sync = () => setStoredValue(readValue());
    // Same-document updates from other instances of this hook.
    window.addEventListener(SYNC_EVENT_PREFIX + key, sync);
    // Cross-tab updates.
    const onStorage = (e) => {
      if (e.key === key) {
        sync();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SYNC_EVENT_PREFIX + key, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, [key, readValue]);

  return [storedValue, setValue];
}
